'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  animations,
  type AnimationName,
  type AnimationTick,
} from '@/lib/ascii-animations';

interface AsciiAnimationProps {
  animation: AnimationName;
  className?: string;
  /** Characters per second — lower = more chill. Default ~60fps via rAF. */
  fps?: number;
}

/**
 * Renders an ASCII animation into a <pre> element, auto-sizing to fill its
 * container. Pass `animation` to pick which one. Not mounted anywhere yet —
 * ready for use in a future step.
 */
export default function AsciiAnimation({
  animation,
  className = '',
  fps,
}: AsciiAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const tickRef = useRef<AnimationTick | null>(null);
  const rafRef = useRef(0);
  const [frame, setFrame] = useState('');

  const measure = useCallback((): { cols: number; rows: number } => {
    const el = containerRef.current;
    if (!el) return { cols: 80, rows: 24 };

    const test = document.createElement('span');
    test.textContent = 'M';
    test.style.cssText =
      'font:12px/1.2 "JetBrains Mono",monospace;position:absolute;visibility:hidden';
    document.body.appendChild(test);
    const cw = test.offsetWidth;
    const ch = test.offsetHeight;
    document.body.removeChild(test);

    const cols = Math.floor(el.clientWidth / cw);
    const rows = Math.floor(el.clientHeight / ch);
    return { cols: Math.max(cols, 10), rows: Math.max(rows, 5) };
  }, []);

  const start = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    const { cols, rows } = measure();
    const factory = animations[animation];
    const tick = factory(cols, rows);
    tickRef.current = tick;

    if (fps && fps > 0) {
      const interval = 1000 / fps;
      let last = 0;
      const loop = (now: number) => {
        if (now - last >= interval) {
          last = now;
          setFrame(tick());
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } else {
      const loop = () => {
        setFrame(tick());
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    }
  }, [animation, fps, measure]);

  useEffect(() => {
    start();
    return () => cancelAnimationFrame(rafRef.current);
  }, [start]);

  useEffect(() => {
    const onResize = () => start();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [start]);

  return (
    <div ref={containerRef} className={`overflow-hidden ${className}`}>
      <pre
        ref={preRef}
        className="font-mono text-[12px] leading-[1.2] whitespace-pre m-0 p-0"
      >
        {frame}
      </pre>
    </div>
  );
}
